import { getAuthToken } from './authProvider';
import {
    animalApiUrl as apiUrl,
    authApiUrl,
    eventsApiUrl,
    newsApiUrl,
    questsApiUrl,
    userApiUrl,
} from './apiConfig';

const createHttpError = async (response, fallbackMessage = 'HTTP error') => {
    let message = `${fallbackMessage}! status: ${response.status}`;
    try {
        const errorBody = await response.json();
        if (errorBody?.detail) {
            message = errorBody.detail;
        }
    } catch (_e) {
        // ignore parse error
    }

    const error = new Error(message);
    error.status = response.status;
    return error;
};

const ensureOk = async (response, fallbackMessage = 'HTTP error') => {
    if (!response.ok) {
        throw await createHttpError(response, fallbackMessage);
    }
    return response;
};

// Приведение чисел/пустых значений к тому, что ждёт бэкенд квестов.
const toIntOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : Math.trunc(n);
};

const questDto = (data) => ({
    title: data.title || '',
    description: data.description || '',
    type: data.type || 'scan_animal',
    target_animal_id: toIntOrNull(data.target_animal_id),
    reward_points: toIntOrNull(data.reward_points) ?? 0,
    image_url: data.image_url || null,
    is_active: data.is_active ?? true,
    // Вопросы квиза: бэкенд ждёт полный набор, у вопроса ровно один правильный вариант
    questions: (data.type === 'quiz' ? (data.questions || []) : []).map((question) => ({
        text: question.text || '',
        options: (question.options || []).map((option) => ({
            text: option.text || '',
            is_correct: !!option.is_correct,
        })),
    })),
});

const rewardDto = (data) => {
    // Все картинки награды; image_url = первая (её показывает приложение)
    const imageUrls = (Array.isArray(data.image_urls) ? data.image_urls : []).filter(Boolean);
    const urls = imageUrls.length ? imageUrls : (data.image_url ? [data.image_url] : []);
    return {
        name: data.name || '',
        description: data.description || '',
        image_url: urls[0] || null,
        image_urls: urls,
        cost_points: toIntOrNull(data.cost_points) ?? 0,
        stock_total: toIntOrNull(data.stock_total) ?? 0,
        max_per_user: toIntOrNull(data.max_per_user),
        is_active: data.is_active ?? true,
    };
};

// Функция для построения правильного URL
const buildUrl = (resource, id = null) => {
    if (resource === 'news') {
        return id ? `${newsApiUrl}/${id}` : `${newsApiUrl}/`;
    }
    
    if (resource === 'events') {
        return id ? `${eventsApiUrl}/${id}` : eventsApiUrl;
    }

    if (resource === 'admin-users') {
        return id ? `${authApiUrl}/admin/users/${id}` : `${authApiUrl}/admin/users`;
    }

    // Сервис квестов (completingquests): задачи, награды, покупки.
    if (resource === 'quests') {
        return id ? `${questsApiUrl}/quests/${id}` : `${questsApiUrl}/quests/`;
    }
    if (resource === 'rewards') {
        return id ? `${questsApiUrl}/rewards/${id}` : `${questsApiUrl}/rewards/`;
    }
    if (resource === 'quest-purchases') {
        return id ? `${questsApiUrl}/purchases/${id}` : `${questsApiUrl}/purchases/`;
    }

    if (resource === 'external-reviews') {
        return id ? `${userApiUrl}/reviews/${id}` : `${userApiUrl}/reviews/`;
    }

    // Для остальных ресурсов используем стандартный apiUrl
    const url = `${apiUrl}/${resource}`;
    return id ? `${url}/${id}` : url;
};

// Функция для получения заголовков с токеном
const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
        'Accept': 'application/json',
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
};

// Функция для фильтрации данных на клиенте
const filterData = (data, filters) => {
    if (!filters || Object.keys(filters).length === 0) {
        return data;
    }

    const result = data.filter(item => {
        return Object.keys(filters).every(key => {
            const filterValue = filters[key];
            if (!filterValue) return true;

            // Обработка вложенных полей
            if (key === 'name') {
                // Для имени используем поиск с toLowerCase
                const searchValue = typeof filterValue === 'string' 
                    ? filterValue.toLowerCase() 
                    : String(filterValue).toLowerCase();
                return item.name?.toLowerCase().includes(searchValue);
            }
            if (key === 'q') {
                const searchValue = String(filterValue).toLowerCase().trim();
                const searchableText = [
                    item.id,
                    item.title,
                    String(item.description || '').replace(/<[^>]*>/g, ' '),
                ].join(' ').toLowerCase();
                return searchableText.includes(searchValue);
            }
            if (key === 'family.name') {
                // Точное совпадение для семейства
                return item.family?.name === filterValue;
            }
            if (key === 'family.order.name') {
                // Точное совпадение для отряда
                return item.family?.order?.name === filterValue;
            }
            if (key === 'family.order.class_.name') {
                // Точное совпадение для класса
                return item.family?.order?.class_?.name === filterValue;
            }

            return true;
        });
    });
    
    return result;
};

// Функция для сортировки данных
const sortData = (data, field, order) => {
    return [...data].sort((a, b) => {
        let aVal, bVal;

        // Получаем значения для сортировки
        if (field === 'name') {
            aVal = a.name;
            bVal = b.name;
        } else if (field === 'family.name') {
            aVal = a.family?.name;
            bVal = b.family?.name;
        } else if (field === 'family.order.name') {
            aVal = a.family?.order?.name;
            bVal = b.family?.order?.name;
        } else if (field === 'family.order.class_.name') {
            aVal = a.family?.order?.class_?.name;
            bVal = b.family?.order?.class_?.name;
        } else {
            aVal = a[field];
            bVal = b[field];
        }

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        const comparison = aVal < bVal ? -1 : 1;
        return order === 'ASC' ? comparison : -comparison;
    });
};

const getPeriodStart = (period) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (period === 'week') {
        const start = new Date(startOfToday);
        start.setDate(start.getDate() - 6);
        return start;
    }

    if (period === 'month') {
        const start = new Date(startOfToday);
        start.setDate(start.getDate() - 29);
        return start;
    }

    if (period === 'today') {
        return startOfToday;
    }

    return null;
};

const isDateInPeriod = (dateValue, period) => {
    if (!dateValue) return false;
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return false;

    const periodStart = getPeriodStart(period);
    if (!periodStart) {
        return true;
    }

    return parsed >= periodStart;
};

export const dataProvider = {
    getList: (resource, params) => {
        if (resource === 'external-reviews') {
            const { page, perPage } = params.pagination;
            const query = new URLSearchParams({
                offset: String((page - 1) * perPage),
                limit: String(perPage),
            });
            Object.entries(params.filter || {}).forEach(([key, value]) => {
                if (value !== '' && value !== null && value !== undefined) query.set(key, value);
            });
            return fetch(`${userApiUrl}/reviews/?${query.toString()}`, { headers: getHeaders() })
                .then((response) => ensureOk(response))
                .then((response) => response.json())
                .then((json) => ({ data: json.items, total: json.total }));
        }

        if (resource === 'guest-users') {
            return Promise.all([
                fetch(`${userApiUrl}/users/`, {
                    method: 'GET',
                    headers: getHeaders(),
                }),
                fetch(`${userApiUrl}/appeals/counts`, {
                    method: 'GET',
                    headers: getHeaders(),
                }),
            ])
                .then(async ([usersResponse, countsResponse]) => {
                    await ensureOk(usersResponse);
                    await ensureOk(countsResponse);

                    const users = await usersResponse.json();
                    const counts = await countsResponse.json();
                    const countMap = new Map(
                        counts.map((item) => [
                            String(item.user_id),
                            {
                                count: item.count || 0,
                                unread_count: item.unread_count || 0,
                            },
                        ])
                    );

                    const enriched = users.map((user) => ({
                        ...user,
                        id: String(user.id),
                        appeals_count: countMap.get(String(user.id))?.count || 0,
                        unread_count: countMap.get(String(user.id))?.unread_count || 0,
                        has_unread: (countMap.get(String(user.id))?.unread_count || 0) > 0,
                    }));

                    // Всегда поднимаем пользователей с непрочитанными наверх.
                    const prioritized = [...enriched].sort((a, b) => {
                        if (a.has_unread !== b.has_unread) {
                            return a.has_unread ? -1 : 1;
                        }
                        return (b.unread_count || 0) - (a.unread_count || 0);
                    });

                    const { page, perPage } = params.pagination;
                    const { field, order } = params.sort;
                    const unreadUsers = prioritized.filter((item) => item.has_unread);
                    const readUsers = prioritized.filter((item) => !item.has_unread);
                    const sortedData = [
                        ...sortData(unreadUsers, field, order),
                        ...sortData(readUsers, field, order),
                    ];
                    const start = (page - 1) * perPage;
                    const end = start + perPage;
                    const paginatedData = sortedData.slice(start, end);

                    return {
                        data: paginatedData,
                        total: sortedData.length,
                    };
                })
                .catch((error) => {
                    console.error('DataProvider error:', error);
                    return Promise.reject(error);
                });
        }

        if (resource === 'guest-appeals') {
            const userId = params.filter?.user_id;
            if (!userId) {
                return Promise.resolve({ data: [], total: 0 });
            }

            return fetch(`${userApiUrl}/appeals/by-user/${userId}`, {
                method: 'GET',
                headers: getHeaders(),
            })
                .then((response) => ensureOk(response))
                .then((response) => response.json())
                .then((json) => ({
                    data: json.map((appeal) => ({ ...appeal, id: String(appeal.id) })),
                    total: json.length,
                }))
                .catch((error) => {
                    console.error('DataProvider error:', error);
                    return Promise.reject(error);
                });
        }

        // Покупки квест-наград: фильтры user_id/status уходят на сервер, пагинация — на клиенте.
        // Имя и телефон гостя живут в userservice, поэтому подмешиваем их по user_id.
        if (resource === 'quest-purchases') {
            const query = new URLSearchParams({ limit: '1000', offset: '0' });
            if (params.filter?.user_id) query.set('user_id', params.filter.user_id);
            if (params.filter?.status) query.set('status', params.filter.status);

            return Promise.all([
                fetch(`${questsApiUrl}/purchases/?${query.toString()}`, {
                    method: 'GET',
                    headers: getHeaders(),
                }).then((response) => ensureOk(response)),
                // Список гостей — вспомогательный: без него выдача всё равно работает.
                fetch(`${userApiUrl}/users/`, {
                    method: 'GET',
                    headers: getHeaders(),
                })
                    .then((response) => (response.ok ? response.json() : []))
                    .catch(() => []),
            ])
                .then(async ([purchasesResponse, users]) => {
                    const purchases = await purchasesResponse.json();
                    const userMap = new Map(
                        (Array.isArray(users) ? users : []).map((user) => [String(user.id), user])
                    );

                    const enriched = purchases.map((purchase) => {
                        const user = userMap.get(String(purchase.user_id));
                        return {
                            ...purchase,
                            user_name: user?.name || '',
                            user_phone: user?.phone || '',
                        };
                    });

                    const { page, perPage } = params.pagination;
                    const { field, order } = params.sort;
                    const sorted = sortData(enriched, field, order);
                    const start = (page - 1) * perPage;
                    return {
                        data: sorted.slice(start, start + perPage),
                        total: sorted.length,
                    };
                })
                .catch((error) => {
                    console.error('DataProvider error:', error);
                    return Promise.reject(error);
                });
        }

        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;

        let url = buildUrl(resource);

        // Новости: приложения ходят в API напрямую и по умолчанию получают только
        // опубликованные. Админке нужны и черновики из телеграма, поэтому явно
        // просим нужный статус (по умолчанию — все).
        if (resource === 'news') {
            const status = params.filter?.status || 'all';
            url = `${url}?status=${encodeURIComponent(status)}`;
        }

        return fetch(url, {
            method: 'GET',
            headers: getHeaders(),
        })
        .then(response => ensureOk(response))
        .then(response => response.json())
        .then(json => {
            if (resource === 'admin-users') {
                return {
                    data: json.data,
                    total: json.total,
                };
            }

            // Фильтруем данные на клиенте
            let filteredData = filterData(json, params.filter);
            
            // Сортируем данные
            filteredData = sortData(filteredData, field, order);

            // Пагинация
            const start = (page - 1) * perPage;
            const end = start + perPage;
            const paginatedData = filteredData.slice(start, end);

            return {
                data: paginatedData,
                total: filteredData.length,
            };
        })
        .catch(error => {
            console.error('DataProvider error:', error);
            return Promise.reject(error);
        });
    },

    getOne: (resource, params) => {
        if (resource === 'dashboard-stats') {
            const period = params?.meta?.period || 'today';
            const reviewQuery = new URLSearchParams({ offset: '0', limit: '1' });
            const reviewPeriodStart = getPeriodStart(period);
            if (reviewPeriodStart) {
                reviewQuery.set('published_from', reviewPeriodStart.toISOString());
            }

            const loadReviewStats = fetch(`${userApiUrl}/reviews/?${reviewQuery.toString()}`, {
                method: 'GET',
                headers: getHeaders(),
            })
                .then((response) => (response.ok ? response.json() : { total: 0 }))
                .catch(() => ({ total: 0 }));

            return Promise.all([
                fetch(`${userApiUrl}/users/`, {
                    method: 'GET',
                    headers: getHeaders(),
                }),
                fetch(`${userApiUrl}/appeals/counts`, {
                    method: 'GET',
                    headers: getHeaders(),
                }),
                loadReviewStats,
            ])
                .then(async ([usersResponse, countsResponse, reviewStats]) => {
                    await ensureOk(usersResponse);
                    await ensureOk(countsResponse);

                    const users = await usersResponse.json();
                    const counts = await countsResponse.json();

                    const newUsersCount = users.filter((user) => (
                        isDateInPeriod(user.registration_date, period)
                    )).length;

                    const unreadAppealsCount = counts.reduce(
                        (sum, item) => sum + (item.unread_count || 0),
                        0
                    );

                    return {
                        data: {
                            id: params.id,
                            period,
                            new_users: newUsersCount,
                            total_users: users.length,
                            new_appeals: unreadAppealsCount,
                            reviews: reviewStats.total || 0,
                        },
                    };
                })
                .catch((error) => {
                    console.error('DataProvider error:', error);
                    return Promise.reject(error);
                });
        }

        const url = buildUrl(resource, params.id);
        return fetch(url, {
            method: 'GET',
            headers: getHeaders(),
        })
        .then(response => ensureOk(response))
        .then(response => response.json())
        .then(json => ({ data: json }));
    },

    getMany: (resource, params) => {
        const query = {
            id: params.ids,
        };
        const url = (resource === 'news' || resource === 'events')
            ? `${buildUrl(resource)}?${new URLSearchParams(query).toString()}`
            : `${apiUrl}/${resource}?${new URLSearchParams(query).toString()}`;
        return fetch(url, {
            method: 'GET',
            headers: getHeaders(),
        })
        .then(response => ensureOk(response))
        .then(response => response.json())
        .then(json => ({ data: json }));
    },

    getManyReference: (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
            ...params.filter,
            [params.target]: params.id,
            _sort: field,
            _order: order,
            _start: (page - 1) * perPage,
            _end: page * perPage,
        };
        const url = (resource === 'news' || resource === 'events')
            ? `${buildUrl(resource)}?${new URLSearchParams(query).toString()}`
            : `${apiUrl}/${resource}?${new URLSearchParams(query).toString()}`;

        return fetch(url, {
            method: 'GET',
            headers: getHeaders(),
        })
        .then(async response => {
            await ensureOk(response);
            const contentRange = response.headers.get('Content-Range');
            const total = contentRange 
                ? parseInt(contentRange.split('/').pop(), 10) 
                : 0;
            
            return response.json().then(json => ({
                data: json,
                total: total || json.length,
            }));
        });
    },

    create: (resource, params) => {
        if (resource === 'external-reviews-import') {
            return fetch(`${userApiUrl}/reviews/import`, {
                method: 'POST',
                headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(params.data.reviews),
            })
                .then((response) => ensureOk(response))
                .then((response) => response.json())
                .then((json) => ({ data: { id: 'import', ...json } }));
        }

        if (resource === 'guest-appeals-mark-read') {
            return fetch(`${userApiUrl}/appeals/by-user/${params.data.user_id}/mark-read`, {
                method: 'POST',
                headers: {
                    ...getHeaders(),
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => ensureOk(response))
                .then((response) => response.json())
                .then((json) => ({ data: json }))
                .catch((error) => {
                    console.error('DataProvider error:', error);
                    return Promise.reject(error);
                });
        }

        // Подтверждение выдачи покупки (purchased -> issued).
        if (resource === 'quest-purchase-issue') {
            return fetch(`${questsApiUrl}/purchases/${params.data.id}/issue`, {
                method: 'POST',
                headers: getHeaders(),
            })
                .then((response) => {
                    if (!response.ok) {
                        return createHttpError(response).then((error) => Promise.reject(error));
                    }
                    return response.json();
                })
                .then((json) => ({ data: json }))
                .catch((error) => {
                    console.error('DataProvider error:', error);
                    return Promise.reject(error);
                });
        }

        const url = buildUrl(resource);

        let requestData = params.data;

        if (resource === 'quests') {
            requestData = questDto(params.data);
        }

        if (resource === 'rewards') {
            requestData = rewardDto(params.data);
        }

        if (resource === 'admin-users') {
            requestData = {
                username: params.data.username,
                password: params.data.password,
                is_active: params.data.is_active ?? true,
            };
        }
        
        // Преобразуем данные в формат AnimalDTO для бэкенда
        if (resource === 'animals') {
            requestData = {
                name: params.data.name,
                family_id: params.data.family?.id || params.data.family_id,
                description: params.data.description || '',
                environment_description: params.data.environment_description || '',
                zoo_description: params.data.zoo_description || '',
                geolocation: params.data.geolocation || [0, 0],
                qr_url: params.data.qr_url || 'http://example.com',
                map_icon_url: params.data.map_icon_url || 'http://example.com',
                list_icon_url: params.data.list_icon_url || 'http://example.com',
                audio_url: params.data.audio_url || 'http://example.com',
            };
        }
        
        // Для новостей преобразуем дату в ISO формат
        if (resource === 'news') {
            requestData = {
                event_id: params.data.event_id || 0,
                employee_id: params.data.employee_id || '',
                event_date: params.data.event_date ? new Date(params.data.event_date).toISOString() : new Date().toISOString(),
                title: params.data.title || '',
                description: params.data.description || '',
                image_url: params.data.image_url || '',
                video_url: params.data.video_url || ''
            };
        }
        
        // Для мероприятий преобразуем дату в ISO формат
        if (resource === 'events') {
            requestData = {
                name: params.data.name || '',
                position: params.data.position || '',
                user_id: params.data.user_id || '',
                event_date: params.data.event_date ? new Date(params.data.event_date).toISOString() : new Date().toISOString(),
                event_time: params.data.event_time || '00:00:00',
                description: params.data.description || '',
                tags: params.data.tags || [],
                image_url: params.data.image_url || ''
            };
        }
        
        return fetch(url, {
            method: 'POST',
            headers: {
                ...getHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        })
        .then(response => {
            if (!response.ok) {
                return createHttpError(response).then((error) => Promise.reject(error));
            }
            return response.json();
        })
        .then(json => ({ data: resource === 'admin-users' ? json : { ...params.data, id: json.id } }));
    },

    update: (resource, params) => {
        const url = buildUrl(resource, params.id);

        let requestData = params.data;

        if (resource === 'external-reviews') {
            requestData = {
                status: params.data.status,
                response_text: params.data.response_text ?? null,
                assignee_id: params.data.assignee_id || null,
            };
        }

        if (resource === 'quests') {
            requestData = questDto(params.data);
        }

        if (resource === 'rewards') {
            requestData = rewardDto(params.data);
        }

        if (resource === 'admin-users') {
            requestData = {
                username: params.data.username,
                is_active: params.data.is_active,
            };

            if (params.data.password) {
                requestData.password = params.data.password;
            }
        }
        
        // Преобразуем данные в формат AnimalDTO для бэкенда
        if (resource === 'animals') {
            requestData = {
                name: params.data.name,
                family_id: params.data.family?.id || params.data.family_id,
                description: params.data.description || '',
                environment_description: params.data.environment_description || '',
                zoo_description: params.data.zoo_description || '',
                geolocation: params.data.geolocation || [0, 0],
                qr_url: params.data.qr_url || 'http://example.com',
                map_icon_url: params.data.map_icon_url || 'http://example.com',
                list_icon_url: params.data.list_icon_url || 'http://example.com',
                audio_url: params.data.audio_url || 'http://example.com'
            };
            
            console.log('=== DataProvider UPDATE ===');
            console.log('URL:', url);
            console.log('Request data:', requestData);
        }
        
        // Для новостей отправляем дату как есть (без дополнительной конвертации)
        if (resource === 'news') {
            requestData = {
                event_id: params.data.event_id || 0,
                employee_id: params.data.employee_id || '',
                event_date: params.data.event_date || new Date().toISOString(),
                title: params.data.title || '',
                description: params.data.description || '',
                image_url: params.data.image_url || '',
                video_url: params.data.video_url || ''
            };
        }
        
        // Для мероприятий отправляем дату как есть (без дополнительной конвертации)
        if (resource === 'events') {
            requestData = {
                name: params.data.name || '',
                position: params.data.position || '',
                user_id: params.data.user_id || '',
                event_date: params.data.event_date || new Date().toISOString(),
                event_time: params.data.event_time || '00:00:00',
                description: params.data.description || '',
                tags: params.data.tags || [],
                image_url: params.data.image_url || ''
            };
        }
        
        return fetch(url, {
            method: 'PATCH',
            headers: {
                ...getHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        })
        .then(response => {
            console.log('Response status:', response.status);
            if (!response.ok) {
                return createHttpError(response).then((error) => Promise.reject(error));
            }
            return response.json();
        })
        .then(json => {
            console.log('Response data:', json);
            if (resource === 'admin-users') {
                return { data: json };
            }
            return { data: { ...params.data, ...json } };
        });
    },

    updateMany: (resource, params) => {
        return Promise.all(
            params.ids.map(id => {
                const url = buildUrl(resource, id);
                return fetch(url, {
                    method: resource === 'news' ? 'PATCH' : 'PUT',
                    headers: {
                        ...getHeaders(),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(params.data),
                });
            })
        ).then(() => ({ data: params.ids }));
    },

    delete: (resource, params) => {
        const url = buildUrl(resource, params.id);
        return fetch(url, {
            method: 'DELETE',
            headers: getHeaders(),
        })
        .then(response => ensureOk(response))
        .then(response => response.json())
        .then(json => ({ data: json }));
    },

    deleteMany: (resource, params) => {
        return Promise.all(
            params.ids.map(id => {
                const url = buildUrl(resource, id);
                return fetch(url, {
                    method: 'DELETE',
                    headers: getHeaders(),
                });
            })
        ).then(() => ({ data: params.ids }));
    },

    // Модерация телеграм-новостей: action = 'publish' | 'reject'.
    setNewsStatus: (id, action) => {
        return fetch(`${newsApiUrl}/${id}/${action}`, {
            method: 'POST',
            headers: getHeaders(),
        })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },

    // Ручная проверка канала, не дожидаясь планового опроса.
    syncTelegram: () => {
        return fetch(`${newsApiUrl}/telegram/sync`, {
            method: 'POST',
            headers: getHeaders(),
        })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },

    syncYandexReviews: () => {
        return fetch(`${userApiUrl}/reviews/sync/yandex?pages=3`, {
            method: 'POST',
            headers: getHeaders(),
        })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },

    syncAllReviews: () => {
        return fetch(`${userApiUrl}/reviews/sync/all`, {
            method: 'POST',
            headers: getHeaders(),
        })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },

    markReviewViewed: (id) => {
        return fetch(`${userApiUrl}/reviews/${id}/view`, {
            method: 'POST',
            headers: getHeaders(),
        })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },

    getReviewSummary: (days = 7) => {
        return fetch(`${userApiUrl}/reviews/summary?days=${days}`, { headers: getHeaders() })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },

    getReviewDuplicates: (id) => {
        return fetch(`${userApiUrl}/reviews/${id}/duplicates`, { headers: getHeaders() })
            .then((response) => ensureOk(response))
            .then((response) => response.json())
            .then((json) => ({ data: json }));
    },
};
